import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotField) {
        formData.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9qrb4nnahb7382ove4g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
        setFormError(serverMsg);
        setFormStatus('error');
      } else {
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const honors = [
    { year: '2025', title: '年度最佳视频平台', org: '中国互联网协会' },
    { year: '2024', title: '最具影响力影视平台', org: '中国影视产业联盟' },
    { year: '2024', title: '用户满意度金奖', org: '消费者权益保护协会' },
    { year: '2023', title: '技术创新奖', org: '中国科技产业协会' },
    { year: '2023', title: '年度十大文化品牌', org: '中国文化产业发展中心' },
  ];

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '好色tv',
    url: 'https://www.haosetv.com',
    description: '专注高清影视内容的正规视频平台',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-8888',
      contactType: 'customer service',
      email: 'kefu@haosetv.com',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '北京市',
      addressCountry: '中国',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '好色tv是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '好色tv是专注高清影视内容的正规视频平台，提供海量电影、电视剧、综艺及短视频资源，支持多端流畅播放。',
        },
      },
      {
        '@type': 'Question',
        name: '如何成为好色tv VIP会员？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以在好色tv官网或App中点击"立即注册"按钮，选择VIP会员套餐并完成支付后即可享受VIP特权。',
        },
      },
      {
        '@type': 'Question',
        name: '好色tv支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '好色tv支持手机、平板、电脑和智能电视等多端播放，观看记录和收藏内容云端同步，随时随地续播。',
        },
      },
      {
        '@type': 'Question',
        name: 'VIP会员有哪些特权？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'VIP会员可享受4K超清画质、杜比全景声、VIP专享内容、多端同步播放、去广告体验、离线下载和专属客服等七大特权。',
        },
      },
    ],
  };

  const webpageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于好色tv',
    description: '好色tv品牌介绍、企业荣誉与联系方式',
    url: 'https://www.haosetv.com/about',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageJsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <section className="mb-10 md:mb-14">
            <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
              关于好色tv
            </h1>
            <div className="relative overflow-hidden rounded-lg aspect-[21/9] mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20streaming%20media%20company%20headquarters%20office%20with%20collaborative%20workspace%20and%20large%20screens%20displaying%20video%20content%2C%20warm%20golden%20and%20amber%20interior%20lighting%2C%20dark%20sophisticated%20atmosphere%2C%20professional%20corporate%20photography%2C%20creative%20tech%20environment&width=1600&height=600&seq=about-hero&orientation=landscape"
                alt="好色tv办公环境"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                <strong className="text-foreground-900">好色tv</strong>成立于2020年，是国内领先的<strong className="text-foreground-900">高清影视内容平台</strong>。我们致力于为用户提供丰富、优质、正版的影视内容，涵盖电影、电视剧、综艺、短视频等多个品类，满足不同用户的观看需求。
              </p>
              <p>
                经过多年的发展，好色tv已积累了超过<strong className="text-foreground-900">10万小时的影视资源</strong>，月活跃用户突破<strong className="text-foreground-900">5000万</strong>。我们与国内外数百家影视内容提供商建立了深度合作关系，确保用户能够第一时间观看到最新、最热门的影视作品。
              </p>
              <p>
                我们始终坚持<strong className="text-foreground-900">"用户第一、内容为王"</strong>的核心理念，通过不断优化的技术架构和产品体验，为用户打造极致的观影享受。好色tv支持4K超高清、杜比全景声等先进播放技术，让用户在家中也能享受影院级的视听盛宴。
              </p>
            </div>
          </section>

          <section id="reputation" className="mb-10 md:mb-14">
            <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              企业荣誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {honors.map((honor) => (
                <div
                  key={`${honor.year}-${honor.title}`}
                  className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/50"
                >
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-500/10">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-award-line text-accent-500"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">
                      {honor.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {honor.year} · {honor.org}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10 md:mb-14">
            <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              企业理念
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-heart-line', title: '用户至上', desc: '一切以用户体验为核心，持续优化产品与服务' },
                { icon: 'ri-shield-check-line', title: '正版承诺', desc: '坚持正版内容授权，为用户提供安全可靠的观影环境' },
                { icon: 'ri-rocket-line', title: '技术创新', desc: '不断探索前沿技术，提升播放品质和平台性能' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="text-center p-5 rounded-lg bg-background-100 border border-background-200/50"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl text-primary-500`}></i>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1.5">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="mb-10 md:mb-14">
            <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-secondary-600"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">客服：kefu@haosetv.com</p>
                    <p className="text-sm text-foreground-500">合作：hezuo@haosetv.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line text-secondary-600"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">客服热线</h4>
                    <p className="text-sm text-foreground-500">400-888-8888</p>
                    <p className="text-xs text-foreground-400 mt-0.5">周一至周日 9:00-21:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">公司地址</h4>
                    <p className="text-sm text-foreground-500">北京市朝阳区望京科技园区好色tv大厦</p>
                  </div>
                </div>
              </div>

              <div>
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-3 p-5 rounded-lg bg-background-100 border border-background-200/50"
                >
                  <h4 className="text-sm font-semibold text-foreground-800 mb-2">发送消息</h4>

                  <div>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="您的姓名"
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="您的邮箱"
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="subject"
                      placeholder="主题"
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="请输入您的消息（最多500字）"
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-center text-green-500">
                      消息已发送成功，我们会尽快回复您！
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-center text-red-400">
                      {formError}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}